import React, { ReactNode, useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import PortalContext, {
  PortalEntryState,
  PortalContextType,
} from "../context/portalContext";
import { DEFAULT_THEME } from "../context/themeContext";
import { ThemeProps } from "../models/theme";
import { useAuth, useTheme } from "../hooks";
import { getStorageToken } from "../utils";
import { entryFromPath } from "../utils/portalEntry";
import { useQueryPortalContext } from "../api/queries";

const isUsable = (value: unknown) =>
  value !== undefined && value !== null && value !== "";

export const composeTheme = (theme?: Partial<ThemeProps> | null): ThemeProps =>
  (Object.keys(DEFAULT_THEME) as (keyof ThemeProps)[]).reduce(
    (composed, key) => ({
      ...composed,
      [key]: isUsable(theme?.[key]) ? theme?.[key] : DEFAULT_THEME[key],
    }),
    {} as ThemeProps
  );

export const PortalProvider = ({ children }: { children: ReactNode }) => {
  const { pathname } = useLocation();
  // Read through the context, not straight out of storage. This exact read is
  // what MPAY-32296 was: above its writer it returned nothing forever, because
  // only a context change re-renders a subtree whose element never changed.
  // Position alone fixes it; reading the context keeps it fixed if the tree
  // ever moves again. Storage stays as the fallback, as VerifyDocument does.
  const { token: contextToken } = useAuth();
  const token = contextToken?.access_token || getStorageToken();
  const { updateTheme } = useTheme();

  // A page name and a payment link are asked for differently, and the address
  // alone says which this is. Asked for as a page name, a link id is a 404.
  const entry = entryFromPath(pathname);
  const slug = entry.kind === "slug" ? entry.slug : undefined;
  const paylink = entry.kind === "paylink" ? entry.paylink : undefined;

  const {
    data,
    isNotFound,
    isExpired,
    isError,
    isWaitingForEntry,
  } = useQueryPortalContext({
    slug,
    paylink,
    authorisation: token || "",
    enabled: !!slug || !!paylink,
  });

  // The tenant's theme is composed over the neutral default before it is
  // handed on, so every field carries a value. themeProvider's updateTheme
  // MERGES rather than replaces, so a partial second theme would otherwise
  // leave the first tenant's colours and logo standing -- cross-tenant
  // branding bleed, one layer above the cache keys 16-03 fixed. Composing
  // here makes the merge a replacement without reaching into that provider.
  //
  // Field by field rather than a blanket spread: a key the server sends as
  // null or "" is PRESENT, so a spread would overwrite a good default with an
  // unusable value and paint an empty CSS colour. `false` is a real value for
  // is_active and must survive, which is why this is not a truthiness test.
  const resolvedTheme = useMemo(() => composeTheme(data?.theme), [data?.theme]);

  useEffect(() => {
    if (data?.theme) updateTheme(resolvedTheme);
    // updateTheme is recreated on every render of themeProvider and is not a
    // meaningful dependency; the theme is what decides whether to push.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolvedTheme, data?.theme]);

  // An address with nothing to look up -- the bare front door -- is settled
  // on the neutral theme, not left spinning. Without this it would sit at
  // "resolving" forever behind a disabled query, which on screen is
  // indistinguishable from a slow one.
  const state: PortalEntryState = entry.kind === "none"
    ? "resolved"
    // An address that can only be a payment link and is not a usable one is
    // settled here: no request could succeed, so none is sent.
    : entry.kind === "invalid"
    ? "not-found"
    : isExpired
    ? "expired"
    : isNotFound
    ? "not-found"
    : data
    ? "resolved"
    // Any other failure is terminal too. Left out, a 500 or a dropped
    // connection stays at "resolving" and the payer never leaves the spinner.
    : isError
    ? "unavailable"
    // A query that CANNOT run is not one that is still running. Left as
    // "resolving" it is a spinner with no request behind it, no error and
    // nothing in the logs -- which is exactly how MPAY-32296 stayed invisible.
    : isWaitingForEntry
    ? "unavailable"
    : "resolving";

  const value = useMemo<PortalContextType>(
    () => ({
      scope: data?.scope ?? null,
      theme: resolvedTheme,
      targetServiceId: data?.target_service_id ?? null,
      homePath: entry.homePath,
      state,
    }),
    [data?.scope, data?.target_service_id, resolvedTheme, entry.homePath, state]
  );

  return (
    <PortalContext.Provider value={value}>{children}</PortalContext.Provider>
  );
};

export default PortalProvider;
